/**
 * EmptyState Component - User-friendly empty state
 * Guides users with clear CTAs when no data is available
 */

import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { Button } from './Button';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
}: EmptyStateProps) {
  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-neutral-100 rounded-2xl mb-6">
        <div className="text-neutral-400">
          {icon || <Inbox className="w-10 h-10" />}
        </div>
      </div>

      <h3 className="text-xl font-semibold text-neutral-900 mb-2">
        {title}
      </h3>

      {description && (
        <p className="text-neutral-600 max-w-sm mx-auto mb-8">
          {description}
        </p>
      )}

      {action && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="primary"
            size="lg"
            onClick={action.onClick}
            icon={action.icon}
          >
            {action.label}
          </Button>

          {secondaryAction && (
            <Button
              variant="outline"
              size="lg"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
